import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import get from 'lodash.get';
import omit from 'lodash.omit';
import { useTranslations } from 'next-intl';

import config from '@/config';
import Select from '@/components/select';
import CountrySelect from '@/components/country-select';
import SearchField from '@/components/search-field';
import SEO from '@/components/seo';
import BrandGrid from '@/components/brand-grid';
import { media, fonts } from '@/themes'
import { iconForCategory, colorForCategory } from '@/utils/category';
import { getFallbackCountry } from '@/utils/country';
import { getAllBrandsAndTopupsFromResults, flagForCountryCode, parseIPAddress, shareImageForCountry } from '@/utils';
import { categoryForSlug, countryForId, countryForSlug, lookupCountry, searchBrands } from '@/api';
import StarredBrands from '@/components/starred-brands';
import NewNetworksBanner from '@/components/new-networks-banner';
const Container = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.maxContentWidth};
  margin-bottom: 5em;
  padding: 0 1em;
`;

const MainContent = styled.div`
  flex: 1;
`;

const Header = styled.div`
  margin: 1em 0 0;
  position: sticky;
  top: 1em;
  z-index: 3;

  ${media.sm`
    margin-bottom: 0;
    position: static;
  `};

  &::before, &::after {
    bottom: 1.5em;
    content: '';
    left: -4em;
    position: absolute;
    right: -4em;
    z-index: -1;

    ${media.sm`
      display: none;
    `};
  }

  &::before {
    background: ${props => props.theme.colors.background.primary};
    height: 4em;

    @supports (backdrop-filter: blur(1.25em)) {
      backdrop-filter: blur(1.25em);
      background: ${props => props.theme.colors.background.primary}AA;
    }
  }

  &::after {
    background: linear-gradient(to top, ${props => props.theme.colors.background.primary}, ${props => props.theme.colors.background.primary}00);
    height: 1.5em;
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.typography.primary};
  font-family: ${({ theme }) => theme.fonts.other};
  font-weight: 600;
  margin: .65em 0 .6em;
`;

const CategoryTitle = styled.h2`
  ${fonts.title}
`;

const SearchContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 99999px;
  box-shadow: ${({ theme }) => theme.shadows.primary};
  display: flex;
  height: 3em;
  margin-bottom: 1em;
  transition-duration: 125ms;
  transition-property: box-shadow;
  will-change: box-shadow;

  ${media.sm`
    align-items: flex-start;
    background: none;
    border-radius: 0;
    box-shadow: none;
    flex-direction: column;
    height: auto;
    margin-bottom: 0;
  `}

  ${({ $isFocused, theme }) => $isFocused && css`
    box-shadow: ${theme.shadows.hover};
  `};

  .search-field {
    ${media.sm`
      background-color: ${({ theme }) => theme.colors.background.card};
      border-radius: 1.5em;
      box-shadow: ${({ theme }) => theme.shadows.primary};
      margin: 1em 0;
      width: 100%;
    `};
  }

  &::after {
    background: linear-gradient(to bottom, ${props => props.theme.colors.background.primary}, ${props => props.theme.colors.background.primary}00);
    bottom: -1em;
    content: '';
    height: 2.5em;
    left: -4em;
    position: absolute;
    right: -4em;
    z-index: -1;

    ${media.sm`
      display: none;
    `};
  }
`;

const SidebarLayout = styled.div`
  align-items: flex-start;
  display: flex;

  ${media.md`
    align-items: unset;
    flex-direction: column;
  `};
`;

const CategoriesColumn = styled.div`
  box-sizing: border-box;
  margin-right: 1.5em;
  margin-top: -2.5em;
  max-height: calc(100vh - 6.25em);
  min-width: 25%;
  overflow-y: auto;
  padding-top: 2.5em;
  position: sticky;
  top: 6.25em;

  ${media.md`
    margin-right: 0;
    overflow-y: unset;
    position: static;
  `};

  .use-custom-scrollbars & {
    /* width */
    ::-webkit-scrollbar {
      width: .45em;
    }

    /* Track */
    ::-webkit-scrollbar-track {
      background: transparent;
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: .22em;
    }
  }

  .use-custom-scrollbars &:hover {
    ::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, .125);
      border-radius: .22em;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, .25);
      border-radius: .22em;
    }
  }
`;

const CategorySelect = styled(Select)`
  @media (min-width: 769px) {
    display: none;
  }

  .select {
    &__control {
      background-color: #FCFCFC;
      border: none;
      border-radius: 1.5em;
      box-shadow: ${({ theme }) => theme.shadows.primary};
      height: 3em;
      padding-left: .75em;

      &--menu-is-open {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
`;

const Categories = styled.ul`
  list-style: none;
  padding: 0;
  font-size: 1em;
  margin: .5em 0em 2em;
`;

const CategoryGroup = styled.h5`
  color: ${({ theme }) => theme.colors.typography.subtle};
  font-family: ${({ theme }) => theme.fonts.other};
  font-size: .85em;
  margin: 0 0 0 1.125em;
  text-transform: uppercase;
`;

const CategoryWrapper = styled.li``;

const Category = styled.span`
  align-items: center;
  border-radius: 1.5em;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0.5em 1em;

  ${({ theme, selected }) => css`
    background: ${selected ? `${theme.colors.primary}AA` : 'none'};
    color: ${selected ? 'white' : theme.colors.typography.primary};
  `};
`;

const OptionSeparator = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, .05);
  margin-bottom: 1.25em;
`;

const CategorySidebarWrapper = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const formatGroupLabel = data => {
  if (data.label === 'Featured') {
    return null;
  }

  return <OptionSeparator />;
};

const Index = ({
  brands,
  category,
  countries,
  country,
  otherBrands,
  searchResults,
  searchTerm
}) => {
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchTerm || '');
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [selectedCategory, setSelectedCategory] = useState(!!category ? category.slug : 'giftcards');
  const [clientSearchResults, setClientSearchResults] = useState(searchResults);
  const [clientBrands, setClientBrands] = useState(brands);
  const [clientOtherBrands, setClientOtherBrands] = useState(otherBrands);
  const router = useRouter();
  const cT = useTranslations('categories');
  const t = useTranslations('buy');
  const setFocus = value => () => setIsFocused(value);
  const featuredCountries = ['US', 'CA', 'GB', 'IN'];
  const shouldShowFavorites = searchQuery.trim().length < 3 && (!selectedCategory || selectedCategory === 'giftcard');

  const formattedCountries = countries
    .map(country => ({
      value: country.id,
      label: `${country.id === 'XG' ? '🌎' : flagForCountryCode(country.id)} ${country.name}`,
      ...country
    }));

  const countrySelectOptions = [
    {
      label: 'Featured',
      options: formattedCountries.filter(country => featuredCountries.includes(country.id))
    },
    {
      label: 'All',
      options: formattedCountries.filter(country => !featuredCountries.includes(country.id))
    }
  ];

  let categoryOptions = [];

  const getGroupName = groupName => {
    return cT(`groups.${groupName}`);
  };

  const getCategoryLabel = category => {
    return cT(category.slug || category.name);
  };

  const getBrandsFromResults = (results, cat) => {
    let newBrands = [];

    if (cat) {
      results.groups.map(group => {
        const matchingCategory = group.categories.filter((c) => c.name === cat.name)[0];

        if (matchingCategory) {
          newBrands = matchingCategory.brands;
        }
      });
    } else {
      const firstCategory = get(results, 'groups[0].categories[0].name');

      if (firstCategory) {
        newBrands = results.groups[0].categories[0].brands;
        delete results.groups[0].categories[0].brands;
        cat = results.groups[0].categories[0];
        if (!cat.slug) {
          cat.slug = cat.name;
        }
      }
    }

    return newBrands;
  };

  if (clientSearchResults) {
    categoryOptions = clientSearchResults.groups.map(group => ({
      label: getGroupName(group.name),
      options: group.categories.map(category => ({
        label: getCategoryLabel(category),
        value: category.slug || category.name
      }))
    }));
  }


  const onChangeCountry = (country) => {
    setSelectedCountry(country);
    setSelectedCategory(null);
    setLoading(true);
    setSearchQuery('');

    router.push(`/buy/${country.slug}`, undefined, { shallow: true });
    return router.push(`/buy/${country.slug}`);
  };

  const selectCategory = categorySlug => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q');
    setSelectedCategory(categorySlug);
    setLoading(true);

    if (category) {
      let url = `/buy/${country.slug}/${categorySlug}`;

      if (query) {
        url = `${url}?q=${query}`;
      }

      router.push(url, undefined, { shallow: true });
      return router.push(url);
    }

    router.push(`/buy/${country.slug}`, undefined, { shallow: true });
    return router.push(`/buy/${country.slug}`);
  };

  const selectedCountryOption = {
    value: selectedCountry.id,
    label: `${selectedCountry.id === 'XG' ? '🌎' : flagForCountryCode(selectedCountry.id)} ${selectedCountry.name}`,
    ...selectedCountry
  };

  const setSearchTerm = e => {
    setSearchQuery(e.target.value);
  };

  const search = async event => {
    setLoading(true);

    const { value } = event.target;
    const { results, otherResults = [] } = await searchBrands({
      countryId: country.id,
      $search: value || undefined,
    });

    setClientBrands(getBrandsFromResults(results));
    setClientSearchResults(results);
    setClientOtherBrands(getAllBrandsAndTopupsFromResults(otherResults));
    setSelectedCategory(null);

    if (value.length) {
      window.history.pushState(null, null, `?q=${encodeURIComponent(value)}`);
    } else {
      window.history.pushState(null, null, `/buy/${country.slug}`);
    }

    if (results.groups.length) {
      setSelectedCategory(results.groups[0].categories[0].slug);
    }
  };

  const handleOnReset = () => {
    setLoading(true);
    setSearchQuery('');
    router.push(`/buy/${country.slug}`, undefined, { shallow: true });
    return router.push(`/buy/${country.slug}`);
  };

  useEffect(() => {
    setLoading(false);
    setClientBrands(brands);
    setClientSearchResults(searchResults);
  }, [brands, searchResults]);

  useEffect(() => {
    setLoading(false);
  }, [clientBrands, clientSearchResults]);

  let seoTitle;
  let seoImageUrl;
  const url = `${config.rootUrl}${router.asPath}`;
  // Only show a custom title and image if we're on a country route
  if (router.query.slug) {
    seoTitle = t('seoTitle', { country: country.name });
    seoImageUrl = shareImageForCountry(country.id);
  }

  return (<>
    <SEO
      title={seoTitle}
      url={url}
      imageUrl={seoImageUrl}
    />
    <NewNetworksBanner />
    <Container>
      <Header>
        <Title>{category ? getCategoryLabel(category) : cT('giftcard')}</Title>
        <SearchContainer $isFocused={isFocused}>
          {countrySelectOptions.length > 0 &&
            <CountrySelect
              aria-label={`${t('selectCountry')}`}
              isSearchable
              instanceId="country-select"
              options={countrySelectOptions}
              value={selectedCountryOption}
              onChange={onChangeCountry}
              placeholder={`${t('selectCountry')}`}
              formatGroupLabel={formatGroupLabel} />
          }
          <SearchField
            onBlur={setFocus(false)}
            onFocus={setFocus(true)}
            search={search}
            searchTerm={searchQuery}
            setSearchTerm={setSearchTerm} />
        </SearchContainer>
      </Header>
      <SidebarLayout>
        {!!categoryOptions.length &&
          <CategoriesColumn>
            {selectedCountry && clientSearchResults &&
              <CategorySelect
                aria-label={`${t('selectCategory')}`}
                instanceId="category-select"
                isSearchable={false}
                options={categoryOptions.map(group => ({
                  ...group,
                  options: group.options.map(option => {
                    return {
                      ...option,
                      label: option.label.replace('categories:', '')
                    };
                  })
                }))}
                value={selectedCategory ? { value: selectedCategory, label: cT(selectedCategory) } : null}
                onChange={option => selectCategory(option.value)}
                placeholder={t('selectCategory')} />
            }
            <CategorySidebarWrapper>
              {categoryOptions.map(group => (
                <div key={group.label}>
                  <CategoryGroup>{group.label.replace('category-groups:', '')}</CategoryGroup>
                  <Categories>
                    {group.options.map(({ label, value }) => {
                      const Icon = iconForCategory({ slug: value });

                      return (
                        <CategoryWrapper key={label}>
                          <Category
                            selected={value === selectedCategory}
                            onClick={() => selectCategory(value)}>
                            {Icon &&
                              <Icon
                                color={value !== selectedCategory ? colorForCategory({ slug: value }) : undefined}
                                size={16}
                                style={{ marginRight: '0.5em' }} />
                            }
                            {label}
                          </Category>
                        </CategoryWrapper>
                      );
                    })}
                  </Categories>
                </div>
              ))}
            </CategorySidebarWrapper>
          </CategoriesColumn>
        }
        <MainContent>
          {shouldShowFavorites && <StarredBrands />}
          <CategoryTitle>
            {
              searchQuery.trim().length >= 3
                ? t('searchResults')
                : category
                  ? getCategoryLabel(category)
                  : cT('giftcard')
            }
          </CategoryTitle>
          <BrandGrid
            brands={clientBrands}
            country={selectedCountry}
            loading={loading}
            onReset={handleOnReset}
            otherBrands={clientOtherBrands}
            showEmptyState
            showOtherBrands={Array.isArray(clientOtherBrands) && !!clientOtherBrands.length && !!searchQuery.trim()}
          />
        </MainContent>
      </SidebarLayout>
    </Container>
  </>
  );
};

export async function getServerSideProps ({ params, query, req, locale }) {
  console.log('getServerSideProps', 'params', params, 'query', query);
  //TODO: Error handling and log to Bugsnag
  const ipAddress = query.ip || parseIPAddress(req);
  const searchTerm = query.q || null;
  // TODO: Add geo lookup and redirect if base route
  let country;
  let category = null;
  let type = undefined;
  if (params.slug) {
    const countrySlug = params.slug[0];
    const categorySlug = params.slug[1] || null;
    country = await countryForSlug(countrySlug);
    if (!!categorySlug) {
      category = await categoryForSlug(categorySlug);
    }
    if (!category) {
      type = categorySlug || null;
      if (categorySlug) {
        category = {
          slug: categorySlug || null,
          name: categorySlug || null
        };
      }
    }
    console.log(countrySlug, country);
    console.log(categorySlug, category);
    if (!country) {
      country = await getFallbackCountry(ipAddress, req);
    }
  } else {
    country = await getFallbackCountry(ipAddress, req);
  }

  const { results, otherResults } = await searchBrands({
    countryId: country.id,
    categoryId: category ? category.id : undefined,
    $search: searchTerm || undefined,
    type
  });

  const omittedFields = [
    'description',
    'redemptionInstructions',
    'redemptionInstructionsUrl',
    'disclaimer',
    'categories',
    'products',
    'websiteUrl',
    'importantNotes',
    'providerId',
    'featured',
    'enabled',
    'discontinued',
    'thirdPartyDisabled',
    'cashbackPercent',
    'redemptionMethods',
    'shortName',
    'country.availableInWallet',
    'country.shortName'
  ];

  results.groups = results.groups.map(group => {
    group.categories = group.categories.map(cat => {
      cat.brands = Object.keys(cat.brands).map(key => omit(cat.brands[key], omittedFields))
      cat.brands.forEach(b => {
        b.country = omit(b.country, ['availableInWallet', 'shortName']);
      });
      let updatedCat = omit(cat, 'createdAt', 'updatedAt', 'description', 'subcategory', 'parentCategory', 'markdownFile');
      return updatedCat;
    });
    return group;
  });
  let brands = [];
  if (category) {
    results.groups.map((group) => {
      const matchingCategory = group.categories.filter((c) => c.name === category.name)[0];
      if (matchingCategory) {
        brands = matchingCategory.brands;
      }
    });
  } else {
    const firstCategory = get(results, 'groups[0].categories[0].name');
    if (firstCategory) {
      brands = results.groups[0].categories[0].brands;
      delete results.groups[0].categories[0].brands;
      category = results.groups[0].categories[0];
      if (!category.slug) {
        category.slug = category.name;
      }
    }
  }

  const otherBrands = omit(getAllBrandsAndTopupsFromResults(otherResults), omittedFields);

  return {
    props: {
      searchTerm,
      country,
      category,
      brands,
      otherBrands,
      searchResults: results,
      hideCurrencies: true,
      messages: require(`@/locales/${locale}.json`)
    }
  };
}

export default Index;
